import { Flags } from "@oclif/core"
import { SentryTraced } from "@sentry/nestjs"
import { z, ZodType } from "zod"

import { SentryCommand } from "../../../types/index.js"
import { NotAuthorizedError } from "../../../types/not-authorized.error.js"
import { watchAnalyzeStatus } from "../../../utils/analyze-status.js"
import { createApiClient } from "../../../utils/http-client.js"

const AnalyzeStatusConfig = z.object({
  designSystemId: z.string().optional(),
})

type AnalyzeStatusConfig = z.infer<typeof AnalyzeStatusConfig>

export default class AnalyzeStatus extends SentryCommand<AnalyzeStatusConfig> {
  static override hidden = true
  static override args = {}
  static override description = "Show code snapshot processing status for current user"
  static override examples = ["<%= config.bin %> <%= command.id %> --designSystemId 123"]
  static override flags = {
    designSystemId: Flags.string({ char: "d", description: "Design system ID" }),
  }

  get commandId(): string {
    return AnalyzeStatus.id
  }

  get configSchema(): ZodType<AnalyzeStatusConfig> {
    return AnalyzeStatusConfig
  }

  @SentryTraced()
  public async run(): Promise<void> {
    const { flags } = await this.parse(AnalyzeStatus)
    const config = this.configService.get()
    const designSystemId = flags.designSystemId ?? config?.designSystemId ?? (await this.promptDesignSystemId())
    let apiClient
    try {
      apiClient = await createApiClient(this.env)
    } catch (error) {
      if (error instanceof NotAuthorizedError && (await this.promptLoginIfInteractive())) {
        apiClient = await createApiClient(this.env)
      } else {
        throw error
      }
    }

    await watchAnalyzeStatus({
      apiClient,
      designSystemId,
      error: message => this.error(message),
      log: message => this.log(message),
    })
  }
}
